import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ToastItem = { id: string; type: 'success' | 'error'; message: string };

type ToastContextValue = {
  push: (t: { type: ToastItem['type']; message: string }) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const push = useCallback((t: { type: ToastItem['type']; message: string }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const item: ToastItem = { id, ...t };
    setToasts((s) => [...s, item]);
    // auto-dismiss
    setTimeout(() => {
      setToasts((s) => s.filter((x) => x.id !== id));
    }, 3000);
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`bg-[#141414] px-4 py-3 rounded-lg shadow-2xl text-sm max-w-sm border flex items-start gap-3 select-none ${
              t.type === 'success' ? 'border-green-500/60' : 'border-red-500/60'
            }`}
            role="status"
            aria-live="polite"
          >
            <div className="flex-1">{t.message}</div>
            <button
              type="button"
              onClick={() => setToasts((s) => s.filter((x) => x.id !== t.id))}
              className="text-on-surface-variant hover:text-on-surface"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export default useToast;
